import type { NextRouter } from "next/router";
import {
	getCookieItem,
	setCookieItem,
} from "./cookie";
import { TCocktail, TResponse, TStringObj } from "./types";
import { MD5 } from "crypto-js";
import moment from "moment";
import { CMSAPI, FP_INTERACTION } from "@/config/api";
import { HttpRequest } from "./request";
import { getPublicEnvConfig } from "@/config/env";
export const getUrlQuery = (query: string): string | null => {
	if (typeof window === 'undefined') return null;
	
	const urlParams = new URLSearchParams(window.location.search);
	const lowerQuery = query.toLowerCase();
	
	for (const [key, value] of urlParams.entries()) {
		if (key.toLowerCase() === lowerQuery) {
			return value;
		}
	}
	return null;
};
export const UUID = () => {
	const T = "8-4-4-8";
	const randomStr = () => Math.random().toString(36).substring(2, 10);
	let str = "";
	T.split("-").forEach(function (e) {
		str += randomStr().substring(0, Number(e)) + "-";
	});
	return str.slice(0, -1);
};

export const getConsumerId = (): string => {
	// const consumerId = GetLocalStorageItem("SiteConsumerId");
	const consumerId = getCookieItem("SiteConsumerId");
	if (consumerId) return consumerId;
	const uuid = UUID().slice(0, 14) + Date.now();
	// SetLocalStorageItem("SiteConsumerId", uuid);
	setCookieItem("SiteConsumerId", uuid, {
		days: 90
	});
	return uuid;
};
export const getSessionToken = (refresh: boolean = false): string => {
	const sessionToken = sessionStorage.getItem("sessionToken");
	if (sessionToken && !refresh) return sessionToken;
	const uuid = UUID()
	sessionStorage.setItem("sessionToken", uuid);
	return uuid;
}

export const QueryToString = (obj: TStringObj): string => {
	return Object.keys(obj)
		.map((key) => {
			const value = obj[key];
			return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
		})
		.join("&");
};
export const CommonRedirect = (router: NextRouter, url: string) => {
	if (!url) return;
	const { locale } = router.query;
	let _url = `/${locale}/${url}`;
	if (url.includes("?")) {
		if (location.search) {
			_url += `&${location.search.slice(1)}`;
		}
	} else {
		_url += `${location.search}`;
	}
	router.push(_url);
};
export const CommonPrefetch = (router: NextRouter, url: string) => {
	if (!url) return;
	const { locale } = router.query;
	let prefetchUrl = `/${locale}/${url}`;
	const search = location.search;
	const requiredParameters = `locale=${locale}&slug=${url}`;
	if (search) {
		prefetchUrl += `${search}&${requiredParameters}`;
	} else {
		prefetchUrl += `?${requiredParameters}`;
	}
	router.prefetch(prefetchUrl);
};
export const GetLocale = () => {
	return location.pathname.split("/")[1];
};
export const ReplaceRichTextLink = (text: string) => {
	if (!text) return text;
	return text.replace(/<a href="\/(.*?)">/g, (_, url) => {
		return `<a href="/${GetLocale()}/${url}${location.search}">`;
	});
};
export const CopyToClipboard = (text: string) => {
	if (navigator.clipboard) {
		navigator.clipboard.writeText(text);
		return;
	}
	const textArea = document.createElement("textarea");
	textArea.value = text;
	document.body.appendChild(textArea);
	textArea.focus();
	textArea.select();
	document.execCommand("copy");
	document.body.removeChild(textArea);
};
export const GetLocalStorageItem = (key: string) => {
	if (!key) return null;
	const value = localStorage.getItem(key);
	if (value) {
		try {
			return JSON.parse(value);
		} catch {
			return value;
		}
	}
	return null;
};
export const SetLocalStorageItem = (key: string, value: any) => {
	localStorage.setItem(key, value);
};
export const GetDiageoCountryCode = (): string => {
	const diageoCookies = getCookieItem("diageo-gateway");
	return diageoCookies?.country || "GB";
};
export const debounce = <T>(func: (...args: T[]) => void, wait: number = 300) => {
	let timeout: any;
	return function (this: any, ...args: T[]) {
		if (timeout) clearTimeout(timeout);
		timeout = setTimeout(() => {
			func.apply(this, args);
		}, wait);
	};
};
export const removeZeroWidthCharacters = (str: string) => {
	if (!str) return str;
	return str.replace(/[\u200B-\u200D\uFEFF]/g, "");
};
export const convertToString = (value: any) => {
	if (typeof value === "string") return value;
	return JSON.stringify(value);
};
/**
 *
 * @param key sessionKey
 * @param dataKey primary key
 * @returns null | any
 */
export const GetSessionStorageItem = (
	key: string,
	dataKey?: any,
	times?: number
): null | any => {
	if (!key) return null;
	const value = sessionStorage.getItem(key);
	if (value) {
		try {
			const result = JSON.parse(value);
			if (dataKey) {
				if (
					Object.hasOwn(result, "date") &&
					Object.hasOwn(result, "key") &&
					Object.hasOwn(result, "value")
				) {
					if (result.key === MD5(convertToString(dataKey)).toString()) {
						// express 2 hours
						if (Date.now() - result.date <= (times || 1000 * 60 * 60 * 2)) {
							return result.value;
						}
						return null;
					}
					return null;
				}
				return null;
			}
			return result;
		} catch {
			return value;
		}
	}
	return null;
};
/**
 *
 * @param key sessionkey
 * @param value sessionValue
 * @param valueKey primary key
 */
export const SetSessionStorageItem = (
	key: string,
	value: any,
	valueKey: any
) => {
	sessionStorage.setItem(
		key,
		JSON.stringify({
			date: Date.now(),
			key: MD5(convertToString(valueKey)).toString(),
			value: value,
		})
	);
};
export const freezeObject = () => {
	[
		// Object,
		// Object.prototype,
		Function,
		Function.prototype,
		Array,
		Array.prototype,
		String,
		String.prototype,
		Number,
		Number.prototype,
		Boolean,
		Boolean.prototype,
	].forEach(Object.freeze);
}
export function isCurrentTimeInDailyRangeIgnoreTimezone(startIso: string, endIso: string): boolean {
	const toSeconds = (iso: string): number => {
		const [h, m, s] = iso.split('T')[1].split(':').map(item => {
			return Number.isNaN(Number(item)) ? 0 : Number(item);
		});
		return h * 3600 + m * 60 + s;
	};

	const start = toSeconds(startIso);
	const end = toSeconds(endIso);
	const now = new Date();
	const current = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
	return current > start && current < end;
}
export function transformDate(dateString: string, format: string = 'h:ma') {
	if (!dateString) {
		return ''
	}
	const date = new Date(dateString)
	if (date.getMinutes() === 0) {
		format = 'ha'
	}
	return moment.utc(dateString).format(format)
}
export function checkAndroidPhone() {
	const ua = navigator.userAgent;
	if (/android/i.test(ua) && /mobile/i.test(ua)) {
		document.body.classList.add('android-phone');
	}

}
export function checkSafari() {
	if (/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
		document.body.classList.add('safari');
	}
}
export function checkBrowser() {
	checkAndroidPhone()
	checkSafari()
}

export async function getCocktailByIds(ids: Array<string>, key: string = 'cocktailList'): Promise<Array<TCocktail>> {
	if (!ids || ids.length === 0) {
		return []
	}
	const cacheList = (GetLocalStorageItem(key ?? 'cocktailList') ?? []) as Array<TCocktail>
	if (cacheList.length > 0) {
		let findCount = 0
		ids.forEach(id => cacheList.find(item => item.id.toLowerCase() === id.toLowerCase()) && findCount++)
		if (findCount === ids.length) {
			return cacheList
		}
	}
	return new Promise((resolve) => {
		HttpRequest.get<TResponse<Array<TCocktail>>>(`${CMSAPI}?locale=${GetLocale()}&contentType=cocktail&title=${ids.join(',')}`).then(res => {
			const { success, data } = res.data
			if (success) {
				const _list = data?.sort((a, b) => b.updateDate - a.updateDate).filter(item => item.enable) ?? []
				SetLocalStorageItem(key ?? 'cocktailList', JSON.stringify(_list))
				resolve(_list)
			}
			resolve([])
		})
	})
}
export async function getCocktailInfo(id: string[], key: string = 'cocktailList'): Promise<Array<TCocktail>> {
	return new Promise((resolve) => {
		const cocktailList = (GetLocalStorageItem(key ?? 'cocktailList') ?? []) as Array<TCocktail>
		const cocktails = cocktailList.filter(item => id.find(idItem => item.id === idItem))
		if (cocktails.length > 0) {
			resolve(cocktails)
			return
		}
		HttpRequest.get<TResponse<Array<TCocktail>>>(`${CMSAPI}?locale=${GetLocale()}&contentType=cocktail&title=${id}`).then(res => {
			const { success, data } = res.data
			if (success) {
				const _list = data?.sort((a, b) => b.updateDate - a.updateDate).filter(item => item.enable) ?? []
				resolve(_list)
			}
			resolve([])
		})
	})
}
export function cleanWebsiteCacheData() {
	sessionStorage.clear()
	localStorage.clear()
}
// 检查是否有上一页路径，如果有则返回true，否则返回false
export function UtilPrevBack(homepage: string = '/home'): Promise<boolean> {
	return new Promise((resolve) => {
		const storage = window?.sessionStorage
		const prevPath = storage.getItem('prevPath');
		const params = new URLSearchParams(location.search);
		params.delete('cocktailId');
		params.delete('noResult');
		const query = params.toString();
		if (prevPath === 'null') {
			window.location.replace(`/${GetLocale()}/${homepage}${query ? '?' + query : ''}`)
			resolve(false)
		}
		if (location.pathname.includes('/home')) {
			window.location.replace(`/${GetLocale()}/landing${query ? '?' + query : ''}`)
			resolve(false)
		}
		resolve(true)
	})
}
export function interaction(sourceId: Array<string>, siteId: string, consumerId: string, interactionType: "Click" | "Pass" | "View" | "Recommended" = "View") {
	sourceId.forEach(id => {
		HttpRequest.post(`${FP_INTERACTION}`, {
			SiteId: siteId,
			sourceId: id,
			interactionType,
			consumerId,
			SessionToken: getSessionToken(),
		})
	})
}
export function uploadLog(level: 'INFO' | 'DEBUG' | 'WARN' | 'ERROR', msg: string) {
    const { BACKEND_HOST } = getPublicEnvConfig()
    fetch(`${BACKEND_HOST}/api/fe/recordLog`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'origin': 'http://localhost:3000',
        },
        body: JSON.stringify({
            LEVEL: level,
            MSG: msg
        }),
    })
}